// ── Alarm snooze length ──────────────────────────────────────────────────────
// How long the Snooze button on a ringing alarm waits before ringing again.
// The native side (Snooze.java) is the source of truth: it reads the value at
// the moment Snooze is tapped, possibly with the app closed, so the choice has
// to live in the native alarm prefs and not only in the WebView.
//
// The localStorage copy exists for the browser preview (alarms can't ring on
// the web, so there is nothing to push to) and as the instant first paint on
// the phone. Its key deliberately lacks the `clarity_` prefix: sync.js mirrors
// those to other devices, and this is a per-device setting.
import { registerPlugin, Capacitor } from '@capacitor/core';

const AlarmScheduler = registerPlugin('AlarmScheduler');

export const snoozeIsNative = Capacitor.isNativePlatform();

// The Snooze row in Settings is Android-only — a browser can't ring an alarm.
// It was switched on for the web briefly so the design could be reviewed on the
// live site; flip to true to bring that back.
export const SHOW_SNOOZE_SETTING_ON_WEB_PREVIEW = false;
export const showSnoozeSetting = snoozeIsNative || SHOW_SNOOZE_SETTING_ON_WEB_PREVIEW;

// Must match Snooze.ALLOWED on the native side.
export const SNOOZE_OPTIONS = [5, 10, 15, 30];
export const DEFAULT_SNOOZE_MINUTES = 10;

const LS_KEY = 'claritySnoozeMinutes';

const clean = (n) => (SNOOZE_OPTIONS.includes(Number(n)) ? Number(n) : DEFAULT_SNOOZE_MINUTES);

export function getStoredSnoozeMinutes() {
  try { return clean(localStorage.getItem(LS_KEY)); } catch { return DEFAULT_SNOOZE_MINUTES; }
}

// The device's real value when running in the app, else the stored one.
export async function loadSnoozeMinutes() {
  if (snoozeIsNative) {
    try {
      const { minutes } = await AlarmScheduler.getSnoozeMinutes();
      return clean(minutes);
    } catch { /* an older build without the plugin method */ }
  }
  return getStoredSnoozeMinutes();
}

export async function saveSnoozeMinutes(minutes) {
  const m = clean(minutes);
  try { localStorage.setItem(LS_KEY, String(m)); } catch { /* private mode etc. */ }
  if (snoozeIsNative) {
    try { await AlarmScheduler.setSnoozeMinutes({ minutes: m }); } catch { /* older build */ }
  }
  return m;
}
